import { useParams } from 'react-router-dom'
import { useCallback, useEffect, useState } from 'react'

import styles from './styles.module.scss'

import { StorageService } from '@/services/storage'
import { InstitutionsService } from '@/services/institutions'
import { Institution } from '@/domain/entities/institution.entity'

import { useDownload } from '@/pages/@hooks/use-download'
import { useUploadLogo } from '@/pages/@hooks/use-upload-logo'
import { useUpdateInstitution } from '../../@hooks/use-update-institution'
import { useListInstitution } from '../../@hooks/use-list-institutions-by-id'

import { Scholar } from '@/Icons'
import { Loader } from '@/components/Loader'
import { RouteType } from '@/services/contracts/storage'
import { imageCoursesStore } from '@/stores/image-courses.store'

const storageService = new StorageService()
const institutionsService = new InstitutionsService()

export function SeeInstitutionDetails() {
  const { id } = useParams()
  const [editing, setEditing] = useState(false)
  const [image, setImage] = useState<File | null>(null)
  const [errorImage, setErrorImage] = useState(false)

  const [updatedInstitution, setUpdatedInstitution] = useState(
    {} as Institution,
  )

  const toggleEditing = () => setEditing((state) => !state)

  const update = async () => {
    if (image) await uploadLogo({ file: image, route: 'logo' })

    updateInstitution({
      id,
      institution: {
        ...institution,
        ...updatedInstitution,
        logo: image ? `logos/institutions/${id}.png` : institution.logo,
        logo_courses: image
          ? `logo_courses/institutions/${id}.png`
          : institution.logo_courses,
        url_image: image
          ? `url_images/institutions/${id}.png`
          : institution.url_image,
      },
    })
    toggleEditing()
  }

  const { setImageCourses, imageCourses } = imageCoursesStore()

  const onSaveImages = (value: File) => setImage(value)

  const { institution, isLoadingInstitution } = useListInstitution({
    institutionsService,
  })

  const { updateInstitution, isPending } = useUpdateInstitution({
    institutionsService,
  })

  const { uploadLogo, isUploading } = useUploadLogo({ storageService })

  const { download, isLoadingDownload } = useDownload({
    storageService,
  })

  const loadUrl = useCallback(async (key: RouteType, urlInput: string) => {
    if (imageCourses?.url && imageCourses.id === +id) return
    const [{ url }] = await download(urlInput)
    setImageCourses({ url, id: +id })
  }, [])

  useEffect(() => {
    const { logo } = institution
    if (logo) loadUrl('logo', logo)
  }, [loadUrl, institution?.logo])

  const initialValue = (key: keyof Institution): string =>
    updatedInstitution[key] !== undefined
      ? (updatedInstitution[key] as string)
      : (institution[key] as string)

  return (
    <>
      {(isLoadingInstitution || isPending || isUploading) && <Loader />}

      {institution && (
        <div className={styles.container}>
          {isLoadingDownload && !imageCourses?.url && !errorImage && <div />}

          <div className={styles.content_image}>
            {imageCourses?.url && !isLoadingDownload && !errorImage && (
              <img
                src={imageCourses?.url}
                width={256}
                alt="Imagem da escola"
                onError={() => setErrorImage(true)}
              />
            )}

            {!isLoadingDownload && (!imageCourses?.url || errorImage) && (
              <Scholar size={400} />
            )}
          </div>

          <div className={styles.content}>
            <h1>Instituição</h1>

            <input
              onChange={(e) =>
                setUpdatedInstitution((state) => ({
                  ...state,
                  title: e.target.value,
                }))
              }
              placeholder="titulo"
              disabled={!editing}
              value={initialValue('title')}
            />
            <input
              onChange={(e) =>
                setUpdatedInstitution((state) => ({
                  ...state,
                  email: e.target.value,
                }))
              }
              placeholder="email"
              disabled={!editing}
              value={initialValue('email')}
            />
            <input
              onChange={(e) =>
                setUpdatedInstitution((state) => ({
                  ...state,
                  password: e.target.value,
                }))
              }
              placeholder="senha"
              disabled={!editing}
              value={initialValue('password')}
            />
            <input
              onChange={(e) =>
                setUpdatedInstitution((state) => ({
                  ...state,
                  bairro: e.target.value,
                }))
              }
              placeholder="bairro"
              disabled={!editing}
              value={initialValue('bairro')}
            />
            <input
              onChange={(e) =>
                setUpdatedInstitution((state) => ({
                  ...state,
                  cidade: e.target.value,
                }))
              }
              placeholder="cidade"
              disabled={!editing}
              value={initialValue('cidade')}
            />
            <input
              onChange={(e) =>
                setUpdatedInstitution((state) => ({
                  ...state,
                  active: e.target.value === 'ativo',
                }))
              }
              placeholder="status"
              disabled={!editing}
              defaultValue={initialValue('active') ? 'ativo' : 'inativo'}
            />
            <input
              onChange={(e) =>
                setUpdatedInstitution((state) => ({
                  ...state,
                  description: e.target.value,
                }))
              }
              placeholder="descrições"
              disabled={!editing}
              value={initialValue('description')}
            />
            <input
              onChange={(e) =>
                setUpdatedInstitution((state) => ({
                  ...state,
                  description_2: e.target.value,
                }))
              }
              placeholder="descrição 2"
              disabled={!editing}
              value={initialValue('description_2')}
            />
            <input
              onChange={(e) =>
                setUpdatedInstitution((state) => ({
                  ...state,
                  description_3: e.target.value,
                }))
              }
              placeholder="descrição 3"
              disabled={!editing}
              value={initialValue('description_3')}
            />

            {editing && (
              <div className={styles.editing}>
                <div className={styles.file_button}>
                  <input
                    id="logo"
                    style={{ display: 'none' }}
                    onChange={(e) => onSaveImages(e.target.files[0])}
                    type="file"
                  />
                  <label htmlFor="logo">Escolher Logo</label>
                  <span style={{ marginLeft: '10px' }}>
                    {image?.name || imageCourses
                      ? new URL(imageCourses?.url).pathname.substring(1)
                      : ''}
                  </span>
                </div>

                <div className={styles.buttons}>
                  <button onClick={update}>Atualizar Dados</button>
                  <button onClick={toggleEditing}>Cancelar</button>
                </div>
              </div>
            )}

            {!editing && (
              <div className={styles.editing}>
                <button onClick={toggleEditing}>Editar</button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  )
}
